<template>
  <PopupInputgroup
    ref="popup"
    class="weight-filter"
    :label="placeholder"
    :append-to="appendTo"
    :disabled="!!disabled"
    hide-edit-icon
    @cancel="onCancel"
    @focus="onFocus"
    @save="onSave"
  >
    <template #popupContent>
      <div class="flex flex-gap-1">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${id}-min`"
          :label="t('common.minParam', { param: t('common.amount') })"
        >
          <InputNumber
            ref="minInput"
            v-model="internalValue.min"
            v-bind="ariaProps"
            :input-id="inputId"
            :class="inputClasses"
            :disabled="!!disabled"
            :placeholder="t('common.minParam', { param: t('common.amount') })"
            @keyup.enter="onSave"
            @keyup.esc="closePopup"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${id}-max`"
          :label="t('common.maxParam', { param: t('common.amount') })"
        >
          <InputNumber
            v-model="internalValue.max"
            v-bind="ariaProps"
            :input-id="inputId"
            :class="inputClasses"
            :disabled="!!disabled"
            :placeholder="t('common.maxParam', { param: t('common.amount') })"
            @keyup.enter="onSave"
            @keyup.esc="closePopup"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${id}-uom`"
          :label="t('uoms.unit')"
        >
          <RygenDropdown
            :id="inputId"
            v-model="internalValue.uomNaturalId"
            v-bind="ariaProps"
            :class="inputClasses"
            :disabled="!!disabled"
            :options="WEIGHT_UNITS"
            :option-label="(uom) => upperCase(t(uom.i18nKey))"
            :placeholder="t('uoms.unit')"
            data-key="id"
            option-value="value"
            filter
            show-clear
          />
        </InputWrapper>
      </div>
    </template>

    <template #placeholder="{ label: placeholderLabel }">
      <slot
        name="placeholder"
        :label="placeholderLabel"
        :placeholder="placeholderValue"
      >
        <InputGroup>
          <input
            :id="id"
            class="p-component p-inputtext w-full"
            :placeholder="placeholderLabel"
            :value="placeholderValue"
            @focus="onFocus"
          />

          <InputGroupAddon>
            <RygenButton
              :aria-label="
                t('common.clearParam', { param: t('search.filters.filters') })
              "
              icon="pi pi-times"
              @click.stop="onClear"
            />
          </InputGroupAddon>
        </InputGroup>
      </slot>
    </template>
  </PopupInputgroup>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { ComponentPublicInstance, PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, useAttrs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { upperCase } from '@rygen/rygen-ui/i18n';
import { WEIGHT_UNITS } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { UomSearchFilter } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import PopupInputgroup from '@rygen/rygen-ui/components/popup/PopupInputgroup.vue';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputNumber from 'primevue/inputnumber';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object as PropType<UomSearchFilter>,
    default: () => new UomSearchFilter(),
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: [Boolean, String, Number, Array, Object],
    default: () => false,
  },
});
const { id, modelValue, appendTo, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: UomSearchFilter): void;
}>();

defineSlots<{
  placeholder: (props: { label: string; placeholder: string }) => any;
}>();

const attrs = useAttrs();
const { t } = useI18n();

const internalValue = ref(new UomSearchFilter(modelValue.value));
const popup: Ref<ComponentPublicInstance | null> = ref(null);
const minInput: Ref<ComponentPublicInstance | null> = ref(null);

const placeholder = computed(
  () => (attrs.placeholder as string) ?? t('lineItems.weight'),
);

const selectedUom = computed(() =>
  WEIGHT_UNITS.find((h) => h.value === internalValue.value.uomNaturalId),
);

const selectedUomName = computed(() =>
  selectedUom.value ? upperCase(t(selectedUom.value.i18nKey)) : undefined,
);

const placeholderValue = computed(() => {
  const { min, max } = modelValue.value;

  if (min === undefined && max === undefined) {
    return selectedUomName.value ?? '';
  }

  const uomText = selectedUomName.value ? `${selectedUomName.value}(s)` : '';

  if (min !== undefined && max === undefined) {
    return `> ${min} ${uomText}`.trim();
  }

  const rangeText = [min ?? 0, max].filter(notNull).join(' - ');
  return `${rangeText} ${uomText}`.trim();
});

function onFocus() {
  if (disabled.value) {
    return;
  }

  nextTick(() => minInput.value?.$el.focus());
}

const onSave = async () => {
  if (!isEqual(internalValue.value, modelValue.value)) {
    emit('update:model-value', internalValue.value);
  }
};

const onCancel = () => {
  internalValue.value = new UomSearchFilter(modelValue.value);
};

const closePopup = () => {
  if (!popup.value) {
    return;
  }

  const popupRef = popup.value as unknown as typeof PopupInputgroup;
  popupRef.onCancel();
};

const onClear = () => {
  internalValue.value = new UomSearchFilter();

  if (!isEqual(internalValue.value, modelValue.value)) {
    emit('update:model-value', internalValue.value);
  }
};

watch(
  modelValue,
  (newVal: UomSearchFilter) => {
    internalValue.value = new UomSearchFilter(newVal);
  },
  { deep: true, immediate: true },
);
</script>
